import React, { useMemo } from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import {
  Home as HomeIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useTheme } from '../../../context/PermissionsContext/ThemeContext/ThemeContext';

interface DealDoorDataProps {
  dealInfo: any;
}

const DealDoorData: React.FC<DealDoorDataProps> = ({ dealInfo }) => {
  const { theme } = useTheme();

  const doorData = useMemo(() => {
    if (!dealInfo) return null;

    try {
      const flexAttrs = dealInfo.flexAttributes
        ? typeof dealInfo.flexAttributes === 'string'
          ? JSON.parse(dealInfo.flexAttributes)
          : dealInfo.flexAttributes
        : {};

      // Extract door/property related data
      const data: Record<string, any> = {};

      // Property info
      if (flexAttrs.Address || flexAttrs.address) {
        data.address = flexAttrs.Address || flexAttrs.address;
      }
      if (flexAttrs.City || flexAttrs.city) {
        data.city = flexAttrs.City || flexAttrs.city;
      }
      if (flexAttrs.State || flexAttrs.state) {
        data.state = flexAttrs.State || flexAttrs.state;
      }
      if (flexAttrs.ZipCode || flexAttrs.zipCode || flexAttrs.zip) {
        data.zipCode = flexAttrs.ZipCode || flexAttrs.zipCode || flexAttrs.zip;
      }

      // Owner info
      if (flexAttrs['Owner Name'] || flexAttrs.ownerName) {
        data.ownerName = flexAttrs['Owner Name'] || flexAttrs.ownerName;
      }
      if (flexAttrs['Owner Occupied'] !== undefined) {
        data.ownerOccupied = flexAttrs['Owner Occupied'];
      }

      // Property details
      if (flexAttrs['Year Built'] || flexAttrs.yearBuilt) {
        data.yearBuilt = flexAttrs['Year Built'] || flexAttrs.yearBuilt;
      }
      if (flexAttrs['Square Feet'] || flexAttrs.squareFeet || flexAttrs.sqft) {
        data.squareFeet =
          flexAttrs['Square Feet'] || flexAttrs.squareFeet || flexAttrs.sqft;
      }
      if (flexAttrs.Bedrooms || flexAttrs.bedrooms) {
        data.bedrooms = flexAttrs.Bedrooms || flexAttrs.bedrooms;
      }
      if (flexAttrs.Bathrooms || flexAttrs.bathrooms) {
        data.bathrooms = flexAttrs.Bathrooms || flexAttrs.bathrooms;
      }
      if (flexAttrs['Property Type'] || flexAttrs.propertyType) {
        data.propertyType =
          flexAttrs['Property Type'] || flexAttrs.propertyType;
      }
      if (flexAttrs['Lot Size'] || flexAttrs.lotSize) {
        data.lotSize = flexAttrs['Lot Size'] || flexAttrs.lotSize;
      }

      // Financial info
      if (flexAttrs['Estimated Value'] || flexAttrs.estimatedValue) {
        data.estimatedValue =
          flexAttrs['Estimated Value'] || flexAttrs.estimatedValue;
      }
      if (flexAttrs.Income || flexAttrs.income) {
        data.income = flexAttrs.Income || flexAttrs.income;
      }

      // Roof info (for roofing companies)
      if (flexAttrs['Roof Type'] || flexAttrs.roofType) {
        data.roofType = flexAttrs['Roof Type'] || flexAttrs.roofType;
      }
      if (flexAttrs['Roof Age'] || flexAttrs.roofAge) {
        data.roofAge = flexAttrs['Roof Age'] || flexAttrs.roofAge;
      }

      return Object.keys(data).length > 0 ? data : null;
    } catch (e) {
      console.error('Error parsing door data:', e);
      return null;
    }
  }, [dealInfo]);

  const formatLabel = (key: string) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatValue = (key: string, value: any) => {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (key.includes('Value') || key.includes('income')) {
      const num =
        typeof value === 'string'
          ? parseFloat(value.replace(/[^0-9.-]/g, ''))
          : value;
      if (!isNaN(num)) {
        return `$${num.toLocaleString()}`;
      }
    }
    if (key.includes('squareFeet') || key.includes('sqft')) {
      return `${Number(value).toLocaleString()} sq ft`;
    }
    return String(value);
  };

  const getIcon = (key: string) => {
    if (key.includes('owner') || key.includes('Name'))
      return <PersonIcon sx={{ fontSize: 18 }} />;
    if (key.includes('Value') || key.includes('income'))
      return <MoneyIcon sx={{ fontSize: 18 }} />;
    if (key.includes('year') || key.includes('Age'))
      return <CalendarIcon sx={{ fontSize: 18 }} />;
    return <HomeIcon sx={{ fontSize: 18 }} />;
  };

  const renderDataItem = (key: string, value: any) => (
    <Grid item xs={12} sm={6} key={key}>
      <Box
        sx={{
          backgroundColor: theme.background2,
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: `${theme.primary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.primary,
            flexShrink: 0,
          }}
        >
          {getIcon(key)}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: 12,
              color: theme.textLight,
              mb: 0.25,
            }}
          >
            {formatLabel(key)}
          </Typography>
          <Typography
            sx={{
              fontSize: 14,
              color: theme.text,
              fontWeight: 500,
            }}
          >
            {formatValue(key, value)}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );

  // Build full address if available
  const fullAddress = useMemo(() => {
    if (!doorData) return null;
    const parts = [
      doorData.address,
      doorData.city,
      doorData.state,
      doorData.zipCode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }, [doorData]);

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: theme.text }}>
          Door Data
        </Typography>
        {doorData?.ownerOccupied !== undefined && (
          <Chip
            label={doorData.ownerOccupied ? 'Owner Occupied' : 'Renter'}
            size="small"
            sx={{
              backgroundColor: doorData.ownerOccupied
                ? '#4CAF5020'
                : '#FF980020',
              color: doorData.ownerOccupied ? '#4CAF50' : '#FF9800',
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      {fullAddress && (
        <Box
          sx={{
            backgroundColor: theme.background2,
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 12, color: theme.textLight, mb: 0.5 }}>
            Property Address
          </Typography>
          <Typography sx={{ fontSize: 16, color: theme.text, fontWeight: 500 }}>
            {fullAddress}
          </Typography>
        </Box>
      )}

      {doorData ? (
        <Grid container spacing={1.5}>
          {Object.entries(doorData)
            .filter(
              ([key]) =>
                ![
                  'address',
                  'city',
                  'state',
                  'zipCode',
                  'ownerOccupied',
                ].includes(key)
            )
            .map(([key, value]) => renderDataItem(key, value))}
        </Grid>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
          }}
        >
          <HomeIcon sx={{ fontSize: 40, color: theme.textLight, mb: 2 }} />
          <Typography sx={{ color: theme.textLight, textAlign: 'center' }}>
            No door data available
          </Typography>
          <Typography
            sx={{
              color: theme.textLight,
              textAlign: 'center',
              fontSize: 12,
              mt: 1,
            }}
          >
            Property information will appear here when available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DealDoorData;
